import { motion } from "framer-motion";
import { useLang } from "../../i18n";
import { Icon } from "../Icon";
import { Marquee } from "../Marquee";
import { FAQS } from "../../data/faq";

const GOLDEN_RULES = [  {
    es: "Agua: 1 galón por persona por día, para 7–14 días.",
    en: "Water: 1 gallon per person per day, for 7–14 days.",
  },
  {
    es: "Vigilancia (watch) = prepárense. Aviso (warning) = actúen ya.",
    en: "Watch = get ready. Warning = act now.",
  },
  {
    es: "Planta eléctrica siempre AFUERA, a más de 20 pies de puertas y ventanas.",
    en: "Generator always OUTSIDE, 20+ feet from doors and windows.",
  },
  {
    es: "Documentos en bolsa impermeable + copias digitales en el correo.",
    en: "Documents in a waterproof bag + digital copies in email.",
  },
  {
    es: "Terremoto: agáchate, cúbrete y agárrate. Costa + sismo fuerte = sube a terreno alto.",
    en: "Earthquake: drop, cover, hold on. Coast + strong quake = move to high ground.",
  },
  {
    es: "Kit de mascotas: comida, agua, medicamentos, jaula y récords.",
    en: "Pet kit: food, water, medications, crate and records.",
  },
];

function waSheetText(lang) {
  const rules = GOLDEN_RULES.map((r) => `• ${r[lang]}`).join("\n");
  return lang === "es"
    ? `HOJA DE EMERGENCIA FAMILIAR — preparatepr.org\n\nTELÉFONOS CLAVE\n• Emergencias: 911\n• NMEAD: 787-724-0124\n• Línea PAS: 1-800-981-0023\n• Policía de PR: 787-343-2020\n• Bomberos: 787-343-2330\n\nREGLAS DE ORO\n${rules}\n\nCompleta tu plan e imprime la hoja en preparatepr.org (pestaña Preguntas)`
    : `FAMILY EMERGENCY SHEET — preparatepr.org\n\nKEY PHONES\n• Emergencies: 911\n• NMEAD: 787-724-0124\n• Línea PAS (emotional crisis): 1-800-981-0023\n• PR Police: 787-343-2020\n• Fire Dept: 787-343-2330\n\nGOLDEN RULES\n${rules}\n\nComplete your plan and print the sheet at preparatepr.org (FAQ tab)`;
}

export function PreguntasTab() {
  const { lang } = useLang();
  return (
    <div data-testid="preguntas-tab">
      <section className="px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="no-print">
          <div className="max-w-4xl flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                {lang === "es" ? "Preguntas Frecuentes" : "Frequently Asked Questions"}
              </h2>
              <p className="mt-2 text-sm sm:text-base text-slate-600">
                {lang === "es"
                  ? "Respuestas directas a las dudas más comunes de preparación ante desastres en Puerto Rico."
                  : "Straight answers to the most common disaster-preparedness questions in Puerto Rico."}
              </p>
            </div>
            <button
              data-testid="faq-print-btn"
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-md border-2 border-[#1C2B4A] px-4 py-2 text-sm font-bold text-[#1C2B4A] hover:bg-[#1C2B4A] hover:text-white transition-colors duration-200 active:scale-[0.98]"
            >
              <Icon name="Printer" className="w-4 h-4" />
              {lang === "es" ? "Imprimir hoja familiar" : "Print family sheet"}
            </button>
            <a
              data-testid="faq-share-wa"
              href={`https://wa.me/?text=${encodeURIComponent(waSheetText(lang))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border-2 border-green-700 px-4 py-2 text-sm font-bold text-green-700 hover:bg-green-700 hover:text-white transition-colors duration-200 active:scale-[0.98]"
            >
              <Icon name="MessageCircle" className="w-4 h-4" />
              WhatsApp
            </a>
          </div>

          <div className="mt-8 max-w-4xl space-y-3">
            {FAQS.map((f, i) => (
              <motion.details
                key={i}
                data-testid={`faq-item-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: Math.min(i * 0.05, 0.3) }}
                className="group rounded-lg border border-slate-300 bg-white open:border-amber-600 open:shadow-md transition-shadow duration-200"
              >
                <summary
                  data-testid={`faq-question-${i}`}
                  className="flex cursor-pointer list-none items-center gap-3 px-5 py-4 font-display font-bold text-base sm:text-lg text-slate-900 hover:text-amber-700 transition-colors duration-150"
                >
                  <Icon name="ChevronRight" className="w-5 h-5 shrink-0 text-amber-700 transition-transform duration-200 group-open:rotate-90" />
                  {f.q[lang]}
                </summary>
                <p data-testid={`faq-answer-${i}`} className="px-5 pb-5 -mt-1 text-sm sm:text-base leading-relaxed text-slate-600" style={{ paddingLeft: "3.25rem" }}>
                  {f.a[lang]}
                </p>
              </motion.details>
            ))}
          </div>

          <div data-testid="faq-help-note" className="mt-10 max-w-4xl rounded-lg border-l-4 border-green-700 border border-slate-300 bg-green-50 p-5 flex items-start gap-3">
            <Icon name="PhoneCall" className="w-6 h-6 text-green-700 shrink-0 mt-0.5" />
            <p className="text-sm sm:text-base font-semibold text-green-900">
              {lang === "es"
                ? "¿Otra duda? Escríbenos desde Sobre Nosotros. En una emergencia inmediata, llama siempre al 911."
                : "Another question? Write to us from About Us. In an immediate emergency, always call 911."}
            </p>
          </div>
        </div>

        {/* Hoja de emergencia familiar — solo impresión (nevera) */}
        <div data-testid="faq-print-sheet" className="print-only print-area">
          <div className="mb-4 flex items-center gap-3 border-b-2 border-slate-800 pb-3">
            <img src="/logo.png" alt="preparatepr.org" className="h-12 w-12 rounded-full" />
            <div>
              <p className="font-display text-xl font-black tracking-tight">preparatepr.org</p>
              <p className="text-xs text-slate-500">
                {lang === "es" ? "Hoja de Emergencia Familiar — pégala en la nevera" : "Family Emergency Sheet — post it on the fridge"}
              </p>
            </div>
          </div>

          <h3 className="text-sm font-black uppercase tracking-wider">Teléfonos clave / Key phones</h3>
          <ul className="mt-1 space-y-0.5 text-xs leading-snug">
            <li><strong>Emergencias / Emergencies: 911</strong></li>
            <li>NMEAD (manejo de emergencias / emergency management): 787-724-0124</li>
            <li>Línea PAS (crisis emocional / emotional crisis): 1-800-981-0023</li>
            <li>Policía de Puerto Rico / PR Police: 787-343-2020</li>
            <li>Bomberos / Fire Department: 787-343-2330</li>
            <li>OMME de mi pueblo / My town’s OMME: ______________________</li>
          </ul>

          <h3 className="mt-4 text-sm font-black uppercase tracking-wider">Reglas de oro / Golden rules</h3>
          <ul className="mt-1 space-y-1 text-xs leading-snug">
            {GOLDEN_RULES.map((r, i) => (
              <li key={i}>
                <strong>• {r.es}</strong>
                <br />
                <span className="text-slate-500">&nbsp;&nbsp;{r.en}</span>
              </li>
            ))}
          </ul>

          <h3 className="mt-4 text-sm font-black uppercase tracking-wider">Nuestro plan / Our plan</h3>
          <ul className="mt-1 space-y-1.5 text-xs leading-snug">
            <li>Punto de encuentro / Meeting point: ______________________</li>
            <li>Contacto fuera de PR / Out-of-state contact: ______________________</li>
            <li>Refugio designado / Designated shelter: ______________________</li>
          </ul>

          <p className="mt-4 border-t border-slate-400 pt-2 text-[10px] text-slate-500">
            preparatepr.org — Puerto Rico, Preparado Siempre. {lang === "es" ? "Clima NWS en vivo, refugios y directorio de los 78 municipios." : "Live NWS weather, shelters and the 78-municipality directory."}
          </p>
        </div>
      </section>

      <div className="no-print">
        <Marquee />
      </div>
    </div>
  );
}
